import { ref } from 'vue';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import {
  sendFavoriteMindboxEvent,
  type FavoriteMindboxExperience,
  type FavoriteMindboxPrice,
} from '@experience/mindbox-events';
import { useExperienceApi } from '@/shared/api/useExperienceApi';
import { useExperienceFavorites } from '@/entities/experience/favorite';

// Берёт Mindbox-поля из общего пакета, исключая несовместимый legacy per_person карточек.
interface FavoriteToggleExperience {
  id: number;
  photos?: FavoriteMindboxExperience['photos'];
  price?: Pick<FavoriteMindboxPrice, 'value' | 'per_group'>;
}

// Предоставляет единый optimistic toggle избранного с отправкой Mindbox перед favorite API.
export function useToggleExperienceFavorite() {
  const { addExperienceToFavorites, removeExperienceFromFavorites } = useExperienceApi();
  const favoritesStore = useExperienceFavorites();
  const isPending = ref(false);

  // Переключает состояние экскурсии, сохраняя callbacks и rollback существующего контракта.
  const toggle = async (
    experience: FavoriteToggleExperience,
    onToggle?: (newVal: boolean) => void,
    onError?: (e: unknown) => void,
  ): Promise<boolean> => {
    const { id } = experience;
    const wasFavorite = favoritesStore.favoriteIds.has(id);

    // Оптимистично обновляет сердце до ответа favorite API.
    if (wasFavorite) {
      favoritesStore.favoriteIds.delete(id);
      onToggle?.(false);
    } else {
      favoritesStore.favoriteIds.add(id);
      onToggle?.(true);
    }

    isPending.value = true;

    try {
      // Фиксирует пользовательское намерение до API через общую реализацию auth/guest payload.
      sendFavoriteMindboxEvent({
        action: wasFavorite ? 'remove' : 'add',
        bootstrapData: getBootstrapData(),
        experience,
      });

      if (wasFavorite) {
        await removeExperienceFromFavorites({ path: { id } });
      } else {
        await addExperienceToFavorites({ path: { id } });
      }

      return true;
    } catch (error) {
      // Откатывает optimistic state и callback при ошибке favorite API.
      if (wasFavorite) {
        favoritesStore.favoriteIds.add(id);
        onToggle?.(true);
      } else {
        favoritesStore.favoriteIds.delete(id);
        onToggle?.(false);
      }
      console.error('Ошибка при переключении избранного:', error);
      onError?.(error);

      return false;
    } finally {
      isPending.value = false;
    }
  };

  return {
    isPending,
    toggle,
  };
}
